import type { ColumnProfile, ColumnAssignment, ColumnChoices, ColumnOverride, ManualPickerRequired, PickerColumn } from './types';

/**
 * The unit a dedicated unit column declares, when it declares one consistently.
 *
 * Returns null when the column carries more than one unit — a mixed-unit export
 * is a different problem, and guessing which row means what is not this
 * function's job.
 */
function declaredUnitOf(profile: ColumnProfile): 'kW' | 'kWh' | 'kVA' | null {
  if (profile.stats.distinctCount !== 1) return null;
  const raw = profile.samples.find(s => s !== null && s !== undefined && String(s).trim() !== '');
  if (raw === undefined) return null;
  return normalizeUnit(raw);
}

/** 'kWh' / 'KWH' / 'kW-h' → 'kWh'. Null for anything not a recognized unit. */
export function normalizeUnit(raw: unknown): 'kW' | 'kWh' | 'kVA' | null {
  if (raw === null || raw === undefined) return null;
  switch (String(raw).trim().toLowerCase().replace(/[^a-z]/g, '')) {
    case 'kwh': return 'kWh';
    case 'kw': return 'kW';
    case 'kva':
    case 'kvar': return 'kVA';
    default: return null;
  }
}

/**
 * The distinct units a unit column names, when it names more than one.
 *
 * Returns an empty array unless at least two different recognized units appear.
 * Read from `stats.distinctValues` rather than `samples`, which stops after five
 * rows: a file that turns to kWh halfway through is the case that matters most.
 */
function rowUnitsOf(profile: ColumnProfile): Array<'kW' | 'kWh' | 'kVA'> {
  const values = profile.stats.distinctValues;
  if (!values) return [];
  const units = new Set<'kW' | 'kWh' | 'kVA'>();
  for (const v of values) {
    const u = normalizeUnit(v);
    if (u) units.add(u);
  }
  return units.size >= 2 ? [...units] : [];
}

/** Human-readable name for a column in a message. */
function labelOf(profile: ColumnProfile): string {
  return profile.header?.trim() || `column ${profile.index + 1}`;
}

/** Every column as the picker previews it, in file order. */
function pickerColumns(profiles: ColumnProfile[]): PickerColumn[] {
  return profiles.map(p => ({ index: p.index, header: p.header, samples: p.samples, kind: p.kind }));
}

/**
 * What a person could choose for each role, plus what the parse chose. The
 * candidate tests are the ones the refusal path uses: columns that read as
 * dates for the timestamp, columns that read as numbers for demand.
 */
export function columnChoices(profiles: ColumnProfile[], chosen: ColumnOverride): ColumnChoices {
  const brief = (p: ColumnProfile) => ({ index: p.index, header: p.header, samples: p.samples });
  return {
    columns: pickerColumns(profiles),
    candidates: {
      timestamp: profiles.filter(p => p.stats.parseableDateCount > 10).map(brief),
      demand: profiles.filter(p => p.stats.numericCount > 10).map(brief),
    },
    chosen,
  };
}

export function assignColumns(
  profiles: ColumnProfile[],
  override?: ColumnOverride,
  opts: { trial?: boolean } = {}
): ColumnAssignment | ManualPickerRequired {
  // A trial of the reading search forces columns the same way a preparer does,
  // but it is detection, not a person's choice: no preparer note, ordinary confidence.
  const preparerChose = !!override && !opts.trial;
  // ── 0. A preparer's own choice outranks detection ─────────────────────────
  // The caller has already checked both indices exist and differ. The chosen
  // columns take the two roles; unit, generation and confidence reasoning below
  // still run against them, and the extractor and invariants treat the result
  // exactly as an automatic one.
  const forcedTs = override ? profiles.find(p => p.index === override.timestampIndex) ?? null : null;
  const forcedDemand = override ? profiles.find(p => p.index === override.demandIndex) ?? null : null;

  // ── 1. Timestamp selection ────────────────────────────────────────────────
  const tsCandidates = forcedTs
    ? [forcedTs]
    : profiles
      .filter(p => p.kind === 'datetime')
      .sort((a, b) => b.confidence - a.confidence);

  if (tsCandidates.length === 0) {
    return {
      __manualPickerRequired: true,
      missing: 'timestamp',
      message: 'TrueLoad could not identify a date/time column in this file.',
      columns: pickerColumns(profiles),
      candidates: {
        timestamp: profiles
          .filter(p => p.stats.parseableDateCount > 10)
          .map(p => ({ index: p.index, header: p.header, samples: p.samples })),
        demand: profiles
          .filter(p => p.stats.numericCount > 10)
          .map(p => ({ index: p.index, header: p.header, samples: p.samples })),
      },
    };
  }

  const warnings: string[] = [];
  let tsProfile = tsCandidates[0];

  // Tie-break when two top candidates are within 0.1 confidence
  if (tsCandidates.length >= 2 && tsCandidates[0].confidence - tsCandidates[1].confidence <= 0.1) {
    const top = tsCandidates.slice(0, 2);
    const localPref = top.find(p => p.header && /local|dttm/i.test(p.header));
    tsProfile = localPref ?? (top[0].index > top[1].index ? top[0] : top[1]);
    const other = tsProfile === top[0] ? top[1] : top[0];
    warnings.push(
      `Multiple timestamp columns detected; selected '${tsProfile.header ?? `column ${tsProfile.index + 1}`}'. Alternative columns are also available.`
    );
    void other;
  }

  // ── 2. Demand selection ───────────────────────────────────────────────────
  const eligible = profiles.filter(p =>
    p.index !== tsProfile.index &&
    !p.headerSignal.excluded &&
    p.kind !== 'numeric-large' &&
    p.kind !== 'numeric-tiny' &&
    p.kind !== 'numeric-other' &&
    p.kind !== 'formula-text' &&
    p.kind !== 'id-like' &&
    p.kind !== 'empty' &&
    p.kind !== 'unknown' &&
    p.kind !== 'datetime' &&
    p.kind !== 'unit-string'
  );

  const demandPool = eligible.filter(p => p.kind === 'numeric-demand');
  const energyPool = eligible.filter(p => p.kind === 'numeric-energy');

  let demandProfile: ColumnProfile | null = null;
  let demandUnit: 'kW' | 'kWh' | 'kVA' = 'kW';

  if (forcedDemand) {
    demandProfile = forcedDemand;
    demandUnit = override?.unit ?? (forcedDemand.headerSignal.isKwh ? 'kWh' : forcedDemand.headerSignal.isKva ? 'kVA' : 'kW');
    if (preparerChose) {
      warnings.push(
        `Columns chosen by the preparer: date/time '${labelOf(tsProfile)}', demand '${labelOf(forcedDemand)}'.`
      );
    }
  } else if (demandPool.length > 0) {
    demandPool.sort((a, b) => {
      const confDiff = b.confidence - a.confidence;
      if (Math.abs(confDiff) > 0.01) return confDiff;
      return b.headerSignal.demandTier - a.headerSignal.demandTier;
    });
    demandProfile = demandPool[0];
    demandUnit = demandProfile.headerSignal.isKva ? 'kVA' : 'kW';
  } else if (energyPool.length > 0) {
    energyPool.sort((a, b) => b.confidence - a.confidence);
    demandProfile = energyPool[0];
    demandUnit = 'kWh';
  }

  if (!demandProfile) {
    return {
      __manualPickerRequired: true,
      missing: 'demand',
      message: 'TrueLoad could not identify a demand (kW, kWh or kVA) column in this file.',
      columns: pickerColumns(profiles),
      candidates: {
        timestamp: [{ index: tsProfile.index, header: tsProfile.header, samples: tsProfile.samples }],
        demand: profiles
          .filter(p => p.stats.numericCount > 10)
          .map(p => ({ index: p.index, header: p.header, samples: p.samples })),
      },
    };
  }

  // ── 3. Generation column ─────────────────────────────────────────────────
  const genProfile = profiles.find(p =>
    p.headerSignal.isGeneration &&
    p.index !== tsProfile.index &&
    p.index !== demandProfile!.index &&
    (p.kind === 'numeric-demand' || p.kind === 'numeric-energy')
  ) ?? null;

  // ── 4. Unit column ────────────────────────────────────────────────────────
  const unitProfile = profiles.find(p =>
    p.kind === 'unit-string' &&
    p.index !== tsProfile.index &&
    p.index !== demandProfile!.index
  ) ?? null;

  // ── 4b. A unit column naming several units means the file states it per row ─
  // The legacy parser converted row by row and reported `powerSource:
  // "mixed_row_units"`; the rebuild dropped it, so a kWh row was read as kW and
  // understated by 60 ÷ interval — 4x on 15-minute data, the direction that
  // wrongly frees capacity. The extractor converts row by row; `demandUnit`
  // below survives only as the fallback for rows whose unit cell is unreadable.
  let mixedRowUnits = false;
  if (unitProfile) {
    const rowUnits = rowUnitsOf(unitProfile);
    if (rowUnits.length >= 2) {
      mixedRowUnits = true;
      warnings.push(
        `Column '${unitProfile.header?.trim() || `column ${unitProfile.index + 1}`}' states a unit on each row ` +
        `(${rowUnits.join(', ')}). Each row was converted using its own stated unit.`
      );
    }
  }

  // ── 4c. A declared unit outranks one inferred from the value header ───────
  // Eversource exports a `Quantity` column plus a `UnitOfMeasure` column reading
  // "kWh". "Quantity" carries no unit signal, so the values were taken as kW and
  // the peak came out at a quarter of its real value — on 2,880 timestamps that
  // overlap the same customer's kW-headed export, the ratio was exactly 4.00.
  // The file's own statement about its units is better evidence than silence.
  if (unitProfile && !mixedRowUnits) {
    const declared = declaredUnitOf(unitProfile);
    if (declared && declared !== demandUnit) {
      warnings.push(
        `Column '${unitProfile.header?.trim() || `column ${unitProfile.index + 1}`}' declares these values as ` +
        `${declared}; the demand column header suggested ${demandUnit}. TrueLoad used ${declared} as declared.`
      );
      demandUnit = declared;
    }
  }

  // ── 5. Overall confidence ─────────────────────────────────────────────────
  // A column the preparer named is not a guess, so it carries no low-confidence
  // warning; the "chosen by the preparer" note above is the disclosure.
  const minConf = Math.min(tsProfile.confidence, demandProfile.confidence);
  const confidence: 'high' | 'medium' | 'low' =
    preparerChose ? 'high' : minConf >= 0.75 ? 'high' : minConf >= 0.5 ? 'medium' : 'low';

  // ── 6. Warnings ───────────────────────────────────────────────────────────
  // Both of these describe the column as a whole. Under mixed row units there is
  // no such thing, and the per-row warning above has already been pushed.
  if (demandUnit === 'kVA' && !mixedRowUnits) {
    warnings.push(
      'Demand column is in kVA. For residential power-factor-near-unity loads, kVA closely approximates kW. Report includes KVA-to-kW disclosure.'
    );
  }
  if (demandUnit === 'kWh' && !mixedRowUnits) {
    warnings.push(
      'Demand column is in kWh (energy). Converted to kW using detected interval. Verify interval is correct in metadata.'
    );
  }
  if (confidence === 'low') {
    warnings.push(
      'Low confidence in column detection. Review parsed result carefully or use manual column picker.'
    );
  }

  // ── 7. Alternatives (low confidence only) ────────────────────────────────
  let alternatives: ColumnAssignment['alternatives'];
  if (confidence === 'low') {
    alternatives = {
      timestamp: tsCandidates.slice(1).map(p => p.index),
      demand: [...demandPool, ...energyPool]
        .filter(p => p.index !== demandProfile!.index)
        .map(p => p.index),
    };
  }

  // ── 8. Rival demand candidates (observational only) ───────────────────────
  // Every viable demand candidate other than the chosen one, for the Layer 2
  // plausibility check. Computed AFTER selection — the pick above is unchanged.
  const rivalCandidates = [...demandPool, ...energyPool]
    .filter(p => p.index !== demandProfile!.index)
    .map(p => ({ index: p.index, header: p.header, kind: p.kind, confidence: p.confidence }));

  return {
    timestampIndex: tsProfile.index,
    demandIndex: demandProfile.index,
    demandUnit,
    generationIndex: genProfile?.index ?? null,
    unitColumnIndex: unitProfile?.index ?? null,
    mixedRowUnits,
    confidence,
    warnings,
    alternatives,
    rivalCandidates,
  };
}
